import {
  AccordionButton,
  AccordionIcon, AccordionItem, AccordionPanel, Box, Button, Divider, Flex,
  HStack,
  Icon,
  IconButton, Link, Menu,
  MenuButton,
  MenuItem,
  MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText, Text, Th, Thead, Tooltip, Tr, useClipboard, useDisclosure, VStack
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import { capitalize, ceil, isNull, isUndefined, noop } from 'lodash'
import moment from 'moment'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BsThreeDots, BsThreeDotsVertical } from 'react-icons/bs'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { FiEdit, FiExternalLink } from 'react-icons/fi'
import { MdOutlineLink } from 'react-icons/md'
import { RiDeleteBinFill } from 'react-icons/ri'
import {
  CellProps,
  Column,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import { checkPermission, PermissionsEnum } from '../../../../utils'
import { DeleteFlagEnum, SortOperation } from '../../../constants'
import useToast from '../../../hooks/useToast'
import {
  PublicLinkResponse, PublicLinksResponse
} from '../../../interfaces'
import { PublicLinksForAClientResponse, SortEnum } from '../../../interfaces/PublicLinks'
import { UserContext } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import { CopyIcon } from '../../CopyIcon'
import usePublicLinkForm from '../../MediaLibrary/PublicLinks/usePublicLinkForm'
import Pagination from '../../Pagination'
import DeleteDialog from './DeleteDialog'

const PublicLinksSettingsComponent = () => {
  const toast = useToast()
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [publicLinkToDelete, setPublicLinkToDelete] = useState<PublicLinkResponse | null>()
  const [publicLinkToEdit, setPublicLinkToEdit] = useState<PublicLinkResponse | null>()
  const [selectedPublicLinks, setSelectedPublicLinks] = useState<PublicLinkResponse[]>([])
  const [deleteFlag, setDeleteFlag] = useState<string>()
  const [publicLinksCount, setPublicLinksCount] = useState<number>(0)
  const [getUrl, setGetUrl] = useState("")
  const [sort, setSort] = useState<string>('')
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const { request, loading } = useFetch<PublicLinksForAClientResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [clientPublicLinks, setClientPublicLinks] = useState<PublicLinkResponse[]>([])
  const {
    formFieldComponents,
    getFormikInitialValues,
    formValidationSchema,
    getPublicLinks,
    createOrUpdatePublicLink,
    deletePublicLink,
  } = usePublicLinkForm({
    request,
  })

  const {
    onCopy
  } = useClipboard("")
  const {
    isOpen: isOpenDeletePublicLink,
    onOpen: onOpenDeletePublicLink,
    onClose: onCloseDeletePublicLink,
  } = useDisclosure()
  const {
    isOpen: isOpenEditPublicLink,
    onOpen: onOpenEditPublicLink,
    onClose: onCloseEditPublicLink,
  } = useDisclosure()

  const handleSelection = useCallback(
    data => {
      selectedPublicLinks.map(value => value._id).includes(data._id)
        ? setSelectedPublicLinks(prev =>
            prev.filter(value => value._id !== data._id),
          )
        : setSelectedPublicLinks(prev => [...prev, data])
    },
    [selectedPublicLinks, setSelectedPublicLinks],
  )

  const columns = useMemo<Array<Column<PublicLinkResponse>>>(
    () => {
      const _columns: Array<Column<PublicLinkResponse>> = [
        {
          Header: t('link', 'Link').toString(),
          disableSortBy: true,
          accessor: values => (
            <HStack verticalAlign="middle" height={"full"}>
              <Link href={values.publicURL} isExternal={true}>
                <Icon verticalAlign={"middle"} as={FiExternalLink} color="appSlate" />
              </Link>
              <Box height={"full"}>
                <CopyIcon
                  extraPropsForIcon={{
                    verticalAlign: "middle",
                  }}
                  textToCopy={values.publicURL}
                />
              </Box>
            </HStack>
          ),
        },
        {
          Header: t('asset_type', 'Asset type').toString(),
          disableSortBy: true,
          accessor: values => (
            <Tooltip label={values.assetInfo.type === "media-collection" ? "Media collection" : capitalize(values.assetInfo.type)}>
              <Text isTruncated>{values.assetInfo.type === "media-collection" ? "Media collection" : capitalize(values.assetInfo.type)}</Text>
            </Tooltip>
          ),
        },
        {
          Header: t('asset_name', 'Asset name').toString(),
          disableSortBy: true,
          accessor: values => (
            <Tooltip label={values.assetInfo.name ?? t("this_asset_was_deleted", "This asset was deleted")}>
              <Text isTruncated>{values.assetInfo.name ?? "-"}</Text>
            </Tooltip>
          ),
        },
        {
          Header: t('valid', 'Valid').toString(),
          maxWidth: 100,
          width: 100,
          disableSortBy: true,
          accessor: values => {
            const isInvalid = (
              typeof values.assetInfo.name === "undefined" ||
              (values.validTill && new Date(values.validTill).getTime() < Date.now()) ||
              (values.validFrom && new Date(values.validFrom).getTime() > Date.now())
            )
            return (
              <Tooltip
                label={
                  <>
                    <Trans t={t} i18nKey="date_colon_separated_with_label">
                      {{ labelString: "Valid from" }}
                      {{ dateString: values.validFrom ? moment(values.validFrom).format('DD MMMM YYYY, hh:mm A') : "-" }}
                    </Trans>
                    <br />
                    <Trans t={t} i18nKey="date_colon_separated_with_label">
                      {{ labelString: "Valid till" }}
                      {{ dateString: values.validTill ? moment(values.validTill).format('DD MMMM YYYY, hh:mm A') : "-" }}
                    </Trans>
                  </>
                }
              >
                <Text color={isInvalid ? "appBrick" : "green"}>
                  {isInvalid ? t("no", "No") : t("yes", "Yes")}
                </Text>
              </Tooltip>
            )
          },
        },
        {
          Header: t('created_on', 'Created on').toString(),
          accessor: values => (
            <Tooltip
              label={moment(values.createdAt).format('DD MMMM YYYY, hh:mm A')}
            >
              <Text cursor="pointer">{moment(values.createdAt).fromNow()}</Text>
            </Tooltip>
          ),
        },
      ]
      
      if (
        !isPublicLink &&
        checkPermission({
          user,
          permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE
        })
      ) {
        _columns.push({
          Header: t('actions', 'Actions').toString(),
          disableSortBy: true,
          Cell: ({ value, row }: CellProps<PublicLinkResponse, string>) => (
            typeof row.original.assetInfo.name !== "undefined" ? (
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  icon={<BsThreeDotsVertical />}
                  variant="link"
                  boxShadow="none"
                />
                <MenuList>
                  <MenuItem
                    icon={<FiEdit />}
                    onClick={() => {
                      setPublicLinkToEdit({ ...row.original })
                      onOpenEditPublicLink()
                    }}
                  >
                    {t('edit', 'Edit')}
                  </MenuItem>
                  <MenuItem
                    icon={<RiDeleteBinFill />}
                    color={colors.appBrick}
                    onClick={() => {
                      setPublicLinkToDelete({ ...row.original })
                      setDeleteFlag(DeleteFlagEnum.SINGLE_DELETE)
                      onOpenDeletePublicLink()
                    }}
                  >
                    {t('delete', 'Delete')}
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : null
          ),
        })
      }

      return _columns
    },
    [selectedPublicLinks, handleSelection, user],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageSize },
    canNextPage,
    pageCount,
    setPageSize,
  } = useTable<PublicLinkResponse>(
    { columns, data: clientPublicLinks },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const generateGetUrl = useCallback(
    ({
      _currentPageIndex,
      _pageSize,
      _sort,
    }: {
      _currentPageIndex: number
      _pageSize: number
      _sort?: string
    }) => {
      let _getUrl = `/api/media-library/${
        user?.clientNumber
      }/public-links?limit=${_pageSize}&skip=${_currentPageIndex * _pageSize}`

      if (_sort) {
        _getUrl = `${_getUrl}&sort=${_sort}`
      }

      setGetUrl(_getUrl)
    },
    [user?.clientNumber, setGetUrl]
  )

  const getPublicLinksSideEffect = useCallback((response: PublicLinksForAClientResponse | PublicLinksResponse | undefined) => {
    if (
      response &&
      "publicLinks" in response &&
      response?.publicLinks &&
      Array.isArray(response?.publicLinks) &&
      !(isUndefined(response.totalPublicLinks) || isNull(response.totalPublicLinks))
    ) {
      setClientPublicLinks(response.publicLinks)
      setPublicLinksCount(response.totalPublicLinks)
    }
  }, [setClientPublicLinks, setPublicLinksCount])

  const localDeletePublicLink = useCallback(async (): Promise<void> => {
    if (
      publicLinkToDelete &&
      user?.clientNumber &&
      ((DeleteFlagEnum.SINGLE_DELETE && publicLinkToDelete?._id) ||
        (DeleteFlagEnum.MULTIPLE_DELETE && selectedPublicLinks.length))
    ) {
        setIsDeleting(true)
        await deletePublicLink({
          publicLink: publicLinkToDelete,
          getUrl,
          sideEffects: getPublicLinksSideEffect,
        })
        setIsDeleting(false)
        setCurrentPageIndex(0)
        setPublicLinkToDelete(null)
        if (deleteFlag === DeleteFlagEnum.MULTIPLE_DELETE) {
          setSelectedPublicLinks([])
          setDeleteFlag('')
        } else if (
          deleteFlag === DeleteFlagEnum.SINGLE_DELETE &&
          selectedPublicLinks.length
        ) {
          setDeleteFlag(DeleteFlagEnum.MULTIPLE_DELETE)
        }
      }
  }, [
    user,
    publicLinkToDelete,
    getUrl, getPublicLinksSideEffect,
    selectedPublicLinks,
    setIsDeleting,
    request,
    deleteFlag,
    toast,
    setCurrentPageIndex,
    getPublicLinks,
    setPublicLinkToDelete,
    setSelectedPublicLinks,
    setDeleteFlag,
  ])

  useEffect(() => {
    generateGetUrl({
      _currentPageIndex: 0,
      _pageSize: 10,
      _sort: undefined
    })
  }, [])

  useEffect(() => {
    if (getUrl) {
      void getPublicLinks({
        url: getUrl,
        sideEffects: getPublicLinksSideEffect,
      })
    }
  }, [getUrl, getPublicLinksSideEffect])


  const handleSort = useCallback(
    column => {
      switch (column) {
        case t('created_on', 'Created on'):
          setSort(prev => {
            const newSort = (
              prev === SortEnum.DESC_CREATED
              ? ''
              : prev === SortEnum.ASC_CREATED
              ? SortEnum.DESC_CREATED
              : SortEnum.ASC_CREATED
            )
            
            setCurrentPageIndex(0)
            generateGetUrl({
              _currentPageIndex: 0,
              _pageSize: pageSize,
              _sort: newSort,
            })

            return newSort
          })
          break
        case t('valid_from', 'Valid from'):
          setSort(prev => {
            const newSort = (
              prev === SortEnum.DESC_VALID_FROM
                ? ''
                : prev === SortEnum.ASC_VALID_FROM
                ? SortEnum.DESC_VALID_FROM
                : SortEnum.ASC_VALID_FROM
            )

            setCurrentPageIndex(0)
            generateGetUrl({
              _currentPageIndex: 0,
              _pageSize: pageSize,
              _sort: newSort,
            })

            return newSort
          })
          break
        case t('valid_till', 'Valid till'):
          setSort(prev => {
            const newSort = (
              prev === SortEnum.DESC_VALID_TILL
              ? ''
              : prev === SortEnum.ASC_VALID_TILL
              ? SortEnum.DESC_VALID_TILL
              : SortEnum.ASC_VALID_TILL
            )

            setCurrentPageIndex(0)
            generateGetUrl({
              _currentPageIndex: 0,
              _pageSize: pageSize,
              _sort: newSort,
            })

            return newSort
          })
          break
        default:
          noop()
      }
    },
    [
      pageSize,
      setSort,
      generateGetUrl,
    ],
  )

  const checkSortState = useCallback(
    (column, opertation, localSort) => {
      switch (column) {
        case t('created_on', 'Created on'):
          return localSort === SortEnum.DESC_CREATED &&
            opertation === SortOperation.DESC
            ? true
            : localSort === SortEnum.ASC_CREATED && opertation === SortOperation.ASC
            ? true
            : false

        case t('valid_from', 'Valid from'):
          return localSort === SortEnum.DESC_VALID_FROM &&
            opertation === SortOperation.DESC
            ? true
            : localSort === SortEnum.ASC_VALID_FROM && opertation === SortOperation.ASC
            ? true
            : false

        case t('valid_till', 'Valid till'):
          return localSort === SortEnum.DESC_VALID_TILL &&
            opertation === SortOperation.DESC
            ? true
            : localSort === SortEnum.ASC_VALID_TILL && opertation === SortOperation.ASC
            ? true
            : false

        default:
          break
      }
    },
    [setSort],
  )

  const pageOptions = useMemo(() => {
    const pages: number[] = []
    if (pageSize && publicLinksCount) {
      for (let x = 0; x < ceil(publicLinksCount / pageSize); x++) pages.push(x)
    }
    return pages
  }, [pageSize, publicLinksCount])

  const generateDeleteObjectText = useCallback(() => {
    return t("the_public_link", "the public link")
  }, [])

  return (
    <AccordionItem>
      <AccordionButton px={12} py={8}>
        <Flex
          flex="1"
          textAlign="left"
          lineHeight="1.5em"
          fontWeight="700"
          style={{ gap: '1em' }}
        >
          <MdOutlineLink fontSize="1.5em" />
          {t('public_links', 'Public links')}
        </Flex>
        <Box background="appPaleGreyThree" borderRadius="50%" padding="0.25em">
          <AccordionIcon color="appSlate" display="block" />
        </Box>
      </AccordionButton>

      <AccordionPanel px={12}>
        <>
          <VStack
            alignItems="start"
            marginTop="1rem"
            width="full"
            spacing={0}
          >
            <Flex align="center" width={'100%'}>
              {!selectedPublicLinks.length && rows.length ? (
                <ChakraText fontWeight="500" marginBottom={"1rem"}>
                  {t('showing', 'Showing')}{' '}
                  {currentPageIndex * pageSize + (pageCount === 0 ? 0 : 1)}{' '}
                  {t('to', 'to')}{' '}
                  {currentPageIndex * pageSize + pageSize <= rows.length
                    ? currentPageIndex * pageSize + pageSize
                    : rows.length + currentPageIndex * pageSize}{' '}
                  {t('of', 'of')} {publicLinksCount} {t('entries', 'entries')}
                </ChakraText>
              ) : null}
              {selectedPublicLinks.length !== 0 && (
                <>
                  <Trans t={t} i18nKey="x_items_selected">
                    <Text
                      color="appGreenishTeal"
                      fontWeight="medium"
                    >
                      {{ numberOfItems: selectedPublicLinks.length }}
                      {` items selected`}
                    </Text>
                  </Trans>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<BsThreeDots />}
                      variant="link"
                      boxShadow="none"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<RiDeleteBinFill />}
                        color={colors.appBrick}
                        onClick={() => {
                          setDeleteFlag(DeleteFlagEnum.MULTIPLE_DELETE)
                          onOpenDeletePublicLink()
                        }}
                      >
                        {t('delete', 'Delete')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </>
              )}
            </Flex>
            <Table {...getTableProps} style={{ tableLayout: "fixed" }}>
              <Thead bgColor="appPaleGreyTwo">
                {headerGroups.map(headerGroup => {
                  const { key, ...restHeaderGroupProps } =
                    headerGroup.getHeaderGroupProps()
                  return (
                    <Tr key={key} {...restHeaderGroupProps}>
                      {headerGroup.headers.map(column => {
                        const { key: keyCol, ...restColumn } =
                          column.getHeaderProps(column.getSortByToggleProps())
                        return (
                          <Th
                            key={keyCol}
                            {...restColumn}
                            maxWidth={column.maxWidth}
                            width={column.width}
                            onClick={() => handleSort(column?.Header)}
                          >
                            {column.render('Header')}
                            {!column.disableSortBy ? (
                              <Box
                                as="span"
                                pl="2"
                                color={colors.appBattleshipGrey}
                              >
                                {checkSortState(
                                  column.Header,
                                  SortOperation.DESC,
                                  sort,
                                ) ? (
                                  <Icon
                                    as={FaSortUp}
                                    aria-label={t(
                                      'sorted_descending',
                                      'sorted descending',
                                    )}
                                  />
                                ) : checkSortState(
                                    column.Header,
                                    SortOperation.ASC,
                                    sort,
                                  ) ? (
                                  <Icon
                                    as={FaSortDown}
                                    aria-label={t(
                                      'sorted_ascending',
                                      'sorted ascending',
                                    )}
                                  />
                                ) : (
                                  <Icon
                                    as={FaSort}
                                    aria-label={t(
                                      'sort_by_column',
                                      'sort by column',
                                    )}
                                  />
                                )}
                              </Box>
                            ) : null}
                          </Th>
                        )
                      })}
                    </Tr>
                  )
                })}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {!loading ? (
                  rows.length ? (
                    page.map(row => {
                      prepareRow(row)
                      const { key, ...restRowProps } = row.getRowProps()
                      return (
                        <Tr key={key} {...restRowProps}>
                          {row.cells.map(cell => {
                            const { key: keyRow, ...restCellProps } =
                              cell.getCellProps()
                            return (
                              <Td key={keyRow} maxWidth={cell.column.maxWidth} width={cell.column.width} {...restCellProps}>
                                <ChakraText maxW="200px">
                                  {cell.render('Cell')}
                                </ChakraText>
                              </Td>
                            )
                          })}
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={6} textAlign="center">
                        <ChakraText>
                          {t(
                            'no_public_links',
                            'No public links found.',
                          )}
                        </ChakraText>
                      </Td>
                    </Tr>
                  )
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                        size="xl"
                      />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
            {pageOptions.length > 0 ? (
              <Divider margin="3em 0em 1em !important" />
            ) : null}
            <Flex width={'100%'}>
              <Spacer />
              <Pagination
                pageIndex={currentPageIndex}
                pageSize={pageSize}
                canPreviousPage={currentPageIndex !== 0}
                canNextPage={
                  publicLinksCount && pageSize
                    ? currentPageIndex + 1 < ceil(publicLinksCount / pageSize)
                    : canNextPage
                }
                pageCount={
                  publicLinksCount && pageSize ? ceil(publicLinksCount / pageSize) : pageSize
                }
                pageOptions={pageOptions}
                setPageSize={(newPageSize) => {
                  setCurrentPageIndex(0)
                  setPageSize(newPageSize)
                  generateGetUrl({
                    _currentPageIndex: 0,
                    _pageSize: newPageSize,
                    _sort: sort,
                  })
                }}
                previousPage={() => {
                  setCurrentPageIndex(prev => {
                    generateGetUrl({
                      _currentPageIndex: (prev !== 0 ? prev - 1 : prev),
                      _pageSize: pageSize,
                      _sort: sort,
                    })
                    return (prev !== 0 ? prev - 1 : prev)
                  })
                }}
                nextPage={() =>
                  setCurrentPageIndex(prev => {
                    generateGetUrl({
                      _currentPageIndex: (prev + 1 < ceil(publicLinksCount / pageSize) ? prev + 1 : prev),
                      _pageSize: pageSize,
                      _sort: sort,
                    })
                    return prev + 1 < ceil(publicLinksCount / pageSize) ? prev + 1 : prev
                  })
                }
                gotoPage={localPage => {
                  setCurrentPageIndex(localPage)
                  generateGetUrl({
                    _currentPageIndex: localPage,
                    _pageSize: pageSize,
                    _sort: sort,
                  })
                }}
              />
            </Flex>
            <DeleteDialog
              isOpen={isOpenDeletePublicLink}
              isDeleting={isDeleting}
              generateDeleteObjectText={generateDeleteObjectText}
              closeDeleteDialog={onCloseDeletePublicLink}
              deletePublicLink={localDeletePublicLink}
            />
            {
              publicLinkToEdit ? (
                <Modal
                  size="xl"
                  isOpen={isOpenEditPublicLink}
                  onClose={onCloseEditPublicLink}
                >
                  <ModalOverlay />
                  <ModalContent>
                    <Formik<{
                      assetType: string
                      validFrom: string | null,
                      validTill: string | null,
                      password?: string,
                      exposeDraftAssets?: boolean,
                    }>
                      key={publicLinkToEdit._id}
                      enableReinitialize
                      initialValues={getFormikInitialValues({
                        publicLink: publicLinkToEdit,
                        assetType: publicLinkToEdit.assetInfo.type,
                      })}
                      validationSchema={formValidationSchema}
                      onSubmit={async (formValues) => {
                        await createOrUpdatePublicLink({
                          formValues,
                          publicLink: publicLinkToEdit,
                          assetId: publicLinkToEdit.assetInfo.id,
                          getUrl,
                          sideEffects: getPublicLinksSideEffect,
                        })
                      }}
                    >
                      {({ dirty, isSubmitting, submitForm }) => (
                        <Form>
                          <ModalHeader>
                            <Icon
                              as={FiEdit}
                              color="appBattleshipGrey"
                              backgroundColor="appPaleGrey"
                              boxSize={6}
                              padding="0.1em"
                              marginRight="0.5em"
                            />
                            {t('edit_public_link', 'Edit public link')}
                          </ModalHeader>
                          <ModalCloseButton />
                          <ModalBody>
                            {formFieldComponents}
                          </ModalBody>
                          <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                            <>
                              <Button onClick={onCloseEditPublicLink} variant="outline">
                                {t('cancel', 'Cancel')}
                              </Button>
                              <Button
                                colorScheme="appBlue"
                                isDisabled={
                                  (publicLinkToEdit && !dirty) ||
                                  isSubmitting
                                }
                                isLoading={isSubmitting}
                                loadingText={t("editing_loading_state_button_label", "Editing...")}
                                onClick={async () => {
                                  onCloseEditPublicLink()
                                  await submitForm()
                                }}
                              >
                                {t('submit', 'Submit')}
                              </Button>
                            </>
                          </ModalFooter>
                        </Form>
                      )}
                    </Formik>
                  </ModalContent>
                </Modal>
              ) : null
            }
          </VStack>
        </>
      </AccordionPanel>
    </AccordionItem>
  )
}

export default PublicLinksSettingsComponent
